import { cn } from "@/lib/utils";

export interface BillReceiptRow {
  label: string;
  value: string;
  // "minus": a saving (green). "note": a reward code (amber, spaced out).
  tone?: "minus" | "note";
  // Small tag after the label, e.g. "Redeeming" on wallet cashback used.
  badge?: string;
}

export interface BillReceiptExtra {
  label: string;
  value: string;
  note?: string;
  // "earn": cashback credited (mint). "gift": a free reward (sun).
  tone: "earn" | "gift";
}

export interface BillReceiptProps {
  title: string;
  rows: BillReceiptRow[];
  total: { label: string; value: string };
  // Fine print under the total.
  foot?: string;
  // Coloured box at the foot (cashback credited, free reward).
  extra?: BillReceiptExtra;
  className?: string;
}

const rowToneClass = {
  minus: "text-tone-mint-ink",
  note: "tracking-[0.06em] text-tone-sun-ink",
};

const extraToneClass = {
  earn: "bg-tone-mint text-tone-mint-ink",
  gift: "bg-tone-sun text-tone-sun-ink",
};

// A paper bill with dotted leaders and a torn zig-zag bottom edge (the
// preview's `.rcpt`). Used by the reward-type tabs and the discount
// calculator. The zig-zag is a CSS mask on the bill itself, so the edge is
// part of the same white shape; the soft shadow sits on a wrapper because a
// mask would clip a box-shadow, and drop-shadow follows the torn outline.
const BillReceipt = ({
  title,
  rows,
  total,
  foot,
  extra,
  className,
}: BillReceiptProps) => (
  <div className={cn("drop-shadow-xl", className)}>
    <div
      className={cn(
        "grid gap-2 rounded-t-2xl bg-white px-[22px] pt-5 pb-[34px] text-ink tabular-nums",
        // Torn edge: 18px teeth, 9px deep, cut out of the bill's bottom.
        "[mask:linear-gradient(black,black)_top/100%_calc(100%-9px)_no-repeat,conic-gradient(from_-45deg_at_bottom,transparent,black_1deg_89deg,transparent_90deg)_bottom/18px_9px_repeat-x]",
      )}
    >
      <div className="mb-0.5 border-b-2 border-dashed border-line pb-2.5">
        <b className="text-[1.1rem] font-bold tracking-[0.08em] uppercase">
          {title}
        </b>
      </div>
      {rows.map((row) => (
        <div
          key={row.label}
          className="flex items-baseline gap-2 text-[15.5px]"
        >
          <span>
            {row.label}
            {row.badge && (
              <span className="ml-1.5 inline-block rounded-full bg-tone-sun px-[9px] py-0.5 align-middle text-[11px] font-extrabold tracking-[0.06em] text-tone-sun-ink uppercase">
                {row.badge}
              </span>
            )}
          </span>
          <i
            aria-hidden="true"
            className="flex-1 -translate-y-1 border-b-2 border-dotted border-line"
          />
          <b
            className={cn(
              "font-bold whitespace-nowrap",
              row.tone && rowToneClass[row.tone],
            )}
          >
            {row.value}
          </b>
        </div>
      ))}
      <div className="mt-1.5 flex items-baseline justify-between gap-4 border-t-2 border-ink pt-2.5 text-[1.2rem] font-extrabold">
        <span>{total.label}</span>
        <b className="whitespace-nowrap text-primary">{total.value}</b>
      </div>
      {foot && <p className="mt-0.5 text-[12.5px] text-ink-muted">{foot}</p>}
      {extra && (
        <div
          className={cn(
            "mt-1.5 grid gap-0.5 rounded-[14px] px-3.5 py-3",
            extraToneClass[extra.tone],
          )}
        >
          <span className="text-[11.5px] font-extrabold tracking-[0.07em] uppercase">
            {extra.label}
          </span>
          <b className="text-[1.5rem] font-bold">{extra.value}</b>
          {extra.note && <small className="text-[13px]">{extra.note}</small>}
        </div>
      )}
    </div>
  </div>
);

export default BillReceipt;
